import React from 'react';
import { Clock, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AdminPropertyActionsMenu } from './AdminPropertyActionsMenu';
import { formatDateDDMMYYYY, formatEuroAmount } from '../../lib/utils';

export const AdminPropertyCard = ({
  property,
  modificationItems,
  pendingVersionItems,
  versionItems,
  publicationRequested,
  hasAnyRequest,
  busyVersionPublishId,
  getTextValue,
  getStatusBadge,
  getPlanBadge,
  busyManageLinkFor,
  busyResendFor,
  onOpenPreview,
  onEditData,
  onClone,
  onPublish,
  onViewSite,
  onEditPublicationUrl,
  onUnpublish,
  onRegisterPayment,
  onEditPayment,
  onUnmarkPaid,
  onGenerateZip,
  onCopyManageLink,
  onResendManageLink,
  onPublishPendingVersion,
  onDelete,
}) => {
  const sortedVersionItems = [...(versionItems || [])].sort((a, b) => (b.version_number || 0) - (a.version_number || 0));
  const latestPublishedVersion = sortedVersionItems.find((item) => item.published || item.ops_status === 'published');
  const hasVersionHistory = sortedVersionItems.length > 0;
  const shouldShowDetailsBlock = hasAnyRequest || hasVersionHistory;

  const formatDateTime = (value) => {
    if (!value) return 'Sin fecha';
    const formatted = formatDateDDMMYYYY(value, 'Sin fecha');
    return formatted;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-xl font-semibold truncate">{getTextValue(property.property_data?.name)}</h3>
                {getStatusBadge(property)}
                {getPlanBadge(property.plan)}
              </div>

              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Licencia:</strong> {property.license_number}</p>
                <p><strong>Ubicacion:</strong> {property.location.google_maps_address}</p>
                <p><strong>Contacto:</strong> {property.contact.email}</p>
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {formatDateDDMMYYYY(property.created_at)}
                </p>
                {property.payment?.paid && (
                  <p className="flex items-center gap-2 text-green-600">
                    <DollarSign className="h-4 w-4" />
                    Pagado: {formatEuroAmount(property.payment.amount, { suffix: 'EUR' })}
                  </p>
                )}
                <p className="text-indigo-700">
                  <strong>Version publicada actual:</strong>{' '}
                  {latestPublishedVersion
                    ? `v${latestPublishedVersion.version_number} (${formatDateTime(latestPublishedVersion.published_at || latestPublishedVersion.updated_at)})`
                    : property.publication?.published
                      ? 'Version inicial (sin historial de versionado)'
                      : 'Sin publicar'}
                </p>
              </div>
            </div>

            <AdminPropertyActionsMenu
              property={property}
              busyManageLinkFor={busyManageLinkFor}
              busyResendFor={busyResendFor}
              onOpenPreview={onOpenPreview}
              onEditData={onEditData}
              onClone={onClone}
              onPublish={onPublish}
              onViewSite={onViewSite}
              onEditPublicationUrl={onEditPublicationUrl}
              onUnpublish={onUnpublish}
              onRegisterPayment={onRegisterPayment}
              onEditPayment={onEditPayment}
              onUnmarkPaid={onUnmarkPaid}
              onGenerateZip={onGenerateZip}
              onCopyManageLink={onCopyManageLink}
              onResendManageLink={onResendManageLink}
              onDelete={onDelete}
            />
          </div>

          {shouldShowDetailsBlock && (
            <div className="pt-3 border-t mt-1">
              <p className="text-xs font-semibold text-gray-700 mb-2">Versiones y solicitudes</p>

              <div className="space-y-2">
                {hasVersionHistory && (
                  <div className="rounded-md border bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">Historial de versiones</p>
                      <Badge variant="secondary">{sortedVersionItems.length}</Badge>
                    </div>
                    <div className="mt-2 space-y-2">
                      {sortedVersionItems.slice(0, 6).map((versionItem) => {
                        const isPublishedVersion = versionItem.published || versionItem.ops_status === 'published';
                        return (
                          <div key={versionItem.id} className="rounded border bg-white/70 px-2 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium truncate">
                                v{versionItem.version_number} - {versionItem.summary || 'Nueva version'}
                              </p>
                              <Badge variant={isPublishedVersion ? 'default' : 'secondary'}>
                                {isPublishedVersion ? 'Publicada' : 'Pendiente'}
                              </Badge>
                            </div>
                            <p className="text-indigo-700 mt-1">
                              {isPublishedVersion
                                ? `Publicada: ${formatDateTime(versionItem.published_at || versionItem.updated_at)}`
                                : `Creada: ${formatDateTime(versionItem.created_at)}`}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pendingVersionItems.length > 0 && (
                  <div className="rounded-md border bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">Versiones pendientes de publicar</p>
                      <Badge variant="secondary">{pendingVersionItems.length}</Badge>
                    </div>
                    <p className="text-amber-700 mt-1">Hay cambios pagados listos para gestion operativa/publicacion.</p>

                    <div className="mt-2 space-y-2">
                      {pendingVersionItems.slice(0, 4).map((versionItem) => (
                        <div key={versionItem.id} className="rounded border bg-white/70 px-2 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium truncate">
                              v{versionItem.version_number} - {versionItem.summary || 'Nueva version'}
                            </p>
                            <Button
                              size="sm"
                              onClick={() => onPublishPendingVersion(versionItem)}
                              disabled={busyVersionPublishId === versionItem.id}
                            >
                              {busyVersionPublishId === versionItem.id ? 'Publicando...' : 'Publicar version'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {publicationRequested && (
                  <div className="rounded-md border bg-blue-50 px-3 py-2 text-xs text-blue-900">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">Solicitud de publicacion pendiente</p>
                      <Badge variant="secondary">Pendiente</Badge>
                    </div>
                    <p className="text-blue-700 mt-1">La propiedad esta pagada y esperando publicacion.</p>
                  </div>
                )}

                {modificationItems.length === 0 ? (
                  !publicationRequested && <p className="text-xs text-gray-500">Sin solicitudes registradas.</p>
                ) : (
                  modificationItems.slice(0, 4).map((requestItem) => (
                    <div key={requestItem.id} className="rounded-md border bg-gray-50 px-3 py-2 text-xs text-gray-700">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium truncate">#{requestItem.id} - {requestItem.summary}</p>
                        <Badge variant={requestItem.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {requestItem.payment_status === 'paid' ? 'Pagada' : 'Pendiente'}
                        </Badge>
                      </div>
                      <p className="text-gray-500 mt-1">
                        {formatDateDDMMYYYY(requestItem.created_at)} - {formatEuroAmount(requestItem.amount, { suffix: 'EUR' })}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
